import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalCompleted: 0,
    totalOngoing: 0,
    totalCounties: 0,
    totalLives: 0
  });
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchProjects();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [filter, heroImages.length]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      let params = {};
      if (filter !== 'all') {
        params.filter = filter;
      }
      
      const response = await axios.get(API_ENDPOINTS.PROJECTS, { params });
      if (response.data.success) {
        setProjects(response.data.data);
        // Calculate statistics after projects are loaded
        calculateStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Optionally set projects to empty array to show no data
      setProjects([]);
      // You could also calculate statistics with empty array if needed
      calculateStatistics([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from projects
  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const calculateStatistics = (projectsData) => {
    const completedProjects = projectsData.filter(p => 
      p.category === 'completed' || p.status === 'completed'
    ).length;
    
    const ongoingProjects = projectsData.filter(p => 
      p.category === 'ongoing' || p.status === 'ongoing'
    ).length;
    
    // Calculate unique counties from all projects
    const allCounties = projectsData.reduce((counties, project) => {
      if (project.counties_covered && project.counties_covered.length > 0) {
        // Make sure counties_covered is an array
        const projectCounties = Array.isArray(project.counties_covered) 
          ? project.counties_covered 
          : [];
        return [...new Set([...counties, ...projectCounties])];
      }
      return counties;
    }, []);
    
    // Calculate total lives impacted - handle both old and new field names
    const totalLives = projectsData.reduce((sum, project) => {
      if (project.total_lives_impacted) {
        return sum + (parseInt(project.total_lives_impacted) || 0);
      } else if (project.lives_impacted) {
        return sum + (parseInt(project.lives_impacted) || 0);
      }
      return sum;
    }, 0);
    
    setStatistics({
      totalCompleted: completedProjects,
      totalOngoing: ongoingProjects,
      totalCounties: allCounties.length,
      totalLives: totalLives
    });
  };

  // Helper function to format number with commas
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Helper to display counties for a project
  const renderCounties = (project) => {
    if (!project.counties_covered || project.counties_covered.length === 0) return null;
    
    // Ensure counties_covered is an array
    const counties = Array.isArray(project.counties_covered) 
      ? project.counties_covered 
      : [];
    
    if (counties.length === 0) return null;
    
    return (
      <div className="d-flex flex-wrap gap-1 mt-2">
        {counties.map((county, index) => (
          <span key={index} className="badge bg-light text-dark border">
            {county}
          </span>
        ))}
      </div>
    );
  };

  // Filter projects based on selected filter
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter || project.status === filter);

  const formatDate = (dateString) => {
    if (!dateString) return 'Ongoing';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="projects-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-3 fw-bold text-white mb-4">Our Projects</h1>
              <p className="lead text-white mb-0">
                Implementing sustainable development initiatives across East Africa
              </p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All Projects
                </button>
                <button 
                  className={`btn ${filter === 'ongoing' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('ongoing')}
                >
                  Ongoing Projects
                </button>
                <button 
                  className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('completed')}
                >
                  Completed Projects
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid - UNCHANGED */}
      <section className="section-padding">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted mb-4"></i>
              <h3>No projects found</h3>
              <p className="text-muted">
                {filter !== 'all' ? `No ${filter} projects at the moment.` : 'Check back later for new projects'}
              </p>
              {filter !== 'all' && (
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => setFilter('all')}
                >
                  View All Projects
                </button>
              )}
            </div>
          ) : (
            <div className="row g-4">
              {filteredProjects.map((project) => (
                <div className="col-lg-4 col-md-6" key={project.id}>
                  <div className="card border-0 shadow-sm hover-card h-100">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3">
                        <span className={`badge ${(project.category === 'ongoing' || project.status === 'ongoing') ? 'bg-info' : 'bg-success'}`}>
                          {(project.category === 'ongoing' || project.status === 'ongoing') ? 'Ongoing' : 'Completed'}
                        </span>
                        {project.thematic_area_name && (
                          <span className="badge bg-secondary ms-2">
                            {project.thematic_area_name}
                          </span>
                        )}
                      </div>
                      
                      <h5 className="card-title">{project.title}</h5>
                      <p className="card-text text-muted flex-grow-1">
                        {project.description || (project.content ? project.content.substring(0, 150) + '...' : 'Project details coming soon...')}
                      </p>
                      
                      <div className="project-details mt-4">
                        {project.location && (
                          <div className="d-flex align-items-center mb-2">
                            <i className="bi bi-geo-alt text-primary me-2"></i>
                            <small>{project.location}</small>
                          </div>
                        )}
                        
                        {/* ADDED: Show counties if available */}
                        {renderCounties(project)}
                        
                        {/* ADDED: Show lives impacted if available */}
                        {(project.total_lives_impacted || project.lives_impacted) && (
                          <div className="d-flex align-items-center mb-2 mt-2">
                            <i className="bi bi-heart-fill text-danger me-2"></i>
                            <small>Lives Impacted: {formatNumber(project.total_lives_impacted || project.lives_impacted)}</small>
                          </div>
                        )}
                        
                        {project.beneficiaries && (
                          <div className="d-flex align-items-center mb-2">
                            <i className="bi bi-people text-primary me-2"></i>
                            <small>Beneficiaries: {project.beneficiaries}</small>
                          </div>
                        )}
                        
                        {project.start_date && (
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar text-primary me-2"></i>
                            <small>
                              {formatDate(project.start_date)}
                              {project.end_date && ` - ${formatDate(project.end_date)}`}
                            </small>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4 pt-3 border-top">
                        <Link 
                          to={`/projects/${project.slug || project.id}`}
                          className="btn btn-outline-primary w-100"
                        >
                          View Details <i className="bi bi-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Statistics - UPDATED to use real data */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row text-center">
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-check-circle display-4 text-success mb-3"></i>
                <h2 className="display-4 fw-bold">{statistics.totalCompleted}</h2>
                <h5 className="fw-normal">Projects Completed</h5>
              </div>
            </div>
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-arrow-clockwise display-4 text-info mb-3"></i>
                <h2 className="display-4 fw-bold">{statistics.totalOngoing}</h2>
                <h5 className="fw-normal">Ongoing Projects</h5>
              </div>
            </div>
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-geo-alt display-4 text-primary mb-3"></i>
                <h2 className="display-4 fw-bold">{statistics.totalCounties}</h2>
                <h5 className="fw-normal">Counties Covered</h5>
              </div>
            </div>
            <div className="col-md-3 mb-4">
              <div className="p-4">
                <i className="bi bi-people display-4 text-warning mb-3"></i>
                <h2 className="display-4 fw-bold">
                  {statistics.totalLives >= 1000000 
                    ? `${(statistics.totalLives / 1000000).toFixed(1)}M+`
                    : formatNumber(statistics.totalLives) + '+'
                  }
                </h2>
                <h5 className="fw-normal">Lives Impacted</h5>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Projects;